import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import {
  JoyajoyImageFile,
  LoadingSpinner,
  WebStoreProductCardProps,
} from '../../components/Molecules';
import { WebStoreProductListTemplate } from '../../components/Templates';
import { DEFAULT_PARTNERID } from '../../constants/appInfo';
import { AppToastInfoOption } from '../../constants/toast';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useLocale from '../../hooks/useLocale';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import {
  ReferralSystem,
  RewardCategory,
  RewardOffer,
  RewardOfferType,
  RewardRedemptionTabState,
  RewardReferralRole,
} from '../../models/RewardStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getLocalCurrencyByRegion } from '../../utils/region';

type State = {
  loadingTotal: number;
  userRewardReferralCode: string | undefined;
};

const { fetchRewardReferral } = useRewardStore.getState();

const WebStoreProductListScreen = () => {
  const navigate = useNavigate();
  const l = useLocale();
  const { t } = useTranslation();

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    userRewardReferralCode: undefined,
  });

  useRedirectEntry();

  const getReferralCode = useCallback(async () => {
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
    const referralCodeData = await useRewardStore
      .getState()
      .getRewardReferralCode(ReferralSystem.Joyajoy);
    setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    if (referralCodeData) {
      setState((s) => ({
        ...s,
        userRewardReferralCode: referralCodeData.referralCode,
      }));
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getReferralCode().catch(console.error);
    }
  }, [getReferralCode, userId]);

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({
            ...s,
            loadingTotal: s.loadingTotal - 1,
          }));
        });
    }
  }, [userId]);

  const rewardReferral = useRewardStore(
    (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
    shallow,
  );

  useEffect(() => {
    if (rewardReferral?.role === RewardReferralRole.Partner) {
      navigate(`/store/role`);
    }
  }, [navigate, rewardReferral?.role]);

  const rewardOfferStore = 'system';
  const allRewardCategoryId =
    RewardCategory.createAll(rewardOfferStore).rewardCategoryId;

  const rewardOffersFieldName = `${rewardOfferStore}_${allRewardCategoryId}`;
  const rewardOffersField = useRewardStoreListField(
    'offers',
    rewardOffersFieldName,
  );

  const fetchRewardOffers = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchOffers(
          rewardOfferStore,
          { referralSystem: ReferralSystem.Joyajoy },
          cursor,
        ),
    [],
  );

  const { fetchAll: fetchAllRewardOffers } = useFetchList(
    rewardOffersField,
    fetchRewardOffers,
  );

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardOffers()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [fetchAllRewardOffers, userId]);

  const rewardOffers = useRewardStore(
    (store) => store.getOffers(rewardOffersFieldName),
    arrayEqual(shallow),
  );

  const webStoreRewardOffers = useMemo(() => {
    return rewardOffers.filter((offer) =>
      [
        // RewardOfferType.ReferralRole,
        // RewardOfferType.ClassroomMembershipTierSet,
        // RewardOfferType.WebPlan,
        // RewardOfferType.WeaMembership,
        RewardOfferType.Token,
      ].includes(offer.reward.type),
    );
  }, [rewardOffers]);

  const rewardRedemptionsFieldName = `${rewardOfferStore}_${allRewardCategoryId}`;
  const rewardRedemptionsField = useRewardStoreListField(
    'redemptions',
    rewardRedemptionsFieldName,
  );

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptions(
          rewardOfferStore,
          RewardRedemptionTabState.Completed,
          cursor,
        ),
    [],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    rewardRedemptionsField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if (userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchAllRewardRedemptions()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [fetchAllRewardRedemptions, userId]);

  const rewardRedemptions = useRewardStore(
    (store) => store.getRedemptions(rewardRedemptionsFieldName),
    arrayEqual(shallow),
  );

  const onClickCard = useCallback(
    (offer: RewardOffer) => {
      navigate(`/store/${offer.rewardOfferId}`);
    },
    [navigate],
  );

  const onClickRoleDetails = useCallback(() => {
    if (rewardReferral?.isPartnerOrDistributorRole) {
      navigate(`/store/role`);
    }
  }, [navigate, rewardReferral?.isPartnerOrDistributorRole]);

  const activeRegion = useSessionStore.getState().activeRegion;
  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyByRegion(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);

  const getPrices = useCallback(
    (
      rewardOffer: RewardOffer,
      isReferralPrice: boolean,
      isReferralReward: boolean,
    ): string | undefined => {
      const priceList = rewardOffer.price;
      const isValid = rewardReferral !== undefined;
      const isRoleRewardType =
        rewardOffer?.reward.type === RewardOfferType.ReferralRole;
      const isCreationTokeOffer =
        rewardOffer?.reward.type === RewardOfferType.Token;

      const rewardRatio = {
        distributor: 0.25,
        normal: 0.1,
      };

      let c = currency;
      let priceNumber =
        isReferralPrice && isValid && !isRoleRewardType && !isCreationTokeOffer
          ? priceList?.[`referral${currency}`]
          : priceList?.[`${currency}`];
      if (priceNumber === undefined) {
        c = 'HKD';
        priceNumber =
          (isReferralPrice &&
          isValid &&
          !isRoleRewardType &&
          !isCreationTokeOffer
            ? priceList?.[`referralHKD`]
            : priceList?.[`HKD`]) ?? 0;
      }

      if (isReferralReward) {
        if (isRoleRewardType) {
          priceNumber =
            rewardOffer.rewardOfferId === DEFAULT_PARTNERID ? 500000 : 300000;
          priceNumber =
            priceNumber *
            (rewardReferral?.role === RewardReferralRole.Partner ? 1.1 : 1);
        } else {
          priceNumber =
            priceNumber *
            (rewardReferral?.role === RewardReferralRole.Distributor
              ? rewardRatio.distributor
              : rewardRatio.normal);
        }
      }

      if (!isValid && (!isReferralPrice || isReferralReward)) {
        return undefined;
      }

      if ((isRoleRewardType || isCreationTokeOffer) && !isReferralPrice) {
        return undefined;
      }

      return `${isReferralReward && isRoleRewardType ? 'HKD' : c}${numbro(
        priceNumber / 100,
      ).format({
        thousandSeparated: true,
        mantissa: 2,
      })}`;
    },
    [rewardReferral, currency],
  );

  const onClickShare = useCallback(
    (rewardOfferId?: string) => {
      const pageUrl = window.location.href;

      const updatedUrl = new URL(pageUrl);
      updatedUrl.pathname = `/store/${rewardOfferId}`;

      const queryParams = new URLSearchParams();
      queryParams.set('referralCode', state.userRewardReferralCode ?? '');
      updatedUrl.search = queryParams.toString();

      if (navigator.share) {
        navigator.share({ url: updatedUrl.toString() }).catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
      } else if (navigator.clipboard) {
        navigator.clipboard
          .writeText(updatedUrl.toString())
          .then(() => {
            // setCopied(true);
            // setTimeout(() => setCopied(false), 2000); // reset after 2s
            toast(t('Link is copied!'), {
              ...AppToastInfoOption,
              id: 'clipboard',
            });
          })
          .catch((err) => {
            console.error('Failed to copy the URL: ', err);
          });
      } else {
        toast(updatedUrl.toString(), AppToastInfoOption);
      }
    },
    [state.userRewardReferralCode, t],
  );

  const productListArray: WebStoreProductCardProps[] = useMemo(() => {
    return webStoreRewardOffers.map((offer) => {
      const i18nInfo = l(offer.i18nRewardOfferInfo);
      const firstImage: JoyajoyImageFile = {
        resource: {
          uri: i18nInfo?.carousel[0]?.image?.publicUrls?.large ?? '',
        },
      };

      return {
        image: firstImage,
        productName: i18nInfo?.name ?? '',
        normalPrice: getPrices(offer, true, false),
        originalPrice: getPrices(offer, false, false),
        description: i18nInfo?.description ?? '',
        referralReward: getPrices(offer, true, true),
        rewardReferralRole: rewardReferral?.role,
        onClickShare: () => {
          onClickShare(offer.rewardOfferId);
        },
        onClickCard: () => {
          onClickCard(offer);
        },
      };
    });
  }, [
    getPrices,
    l,
    onClickCard,
    onClickShare,
    rewardReferral?.role,
    webStoreRewardOffers,
  ]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      <WebStoreProductListTemplate
        profile={userProfile}
        rewardReferralRole={rewardReferral?.role}
        userRewardReferralCode={
          rewardReferral?.isPartnerOrDistributorRole
            ? state.userRewardReferralCode
            : undefined
        }
        productInfoList={productListArray}
        onClickDistributorDetails={
          rewardReferral?.isDistributorRole ? onClickRoleDetails : undefined
        }
      />
    </>
  );
};

export default WebStoreProductListScreen;
